import React from "react";
import { Box, Typography } from "@mui/material";
import { getIn } from "formik";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import "react-multi-date-picker/styles/layouts/mobile.css";

function DatePickerInput({
  value,
  onFocus,
  onChange,
  placeholder,
  disabled,
  hasError,
  helperText,
}) {
  return (
    <Box sx={{ mb: 4 }}>
      <Box
        component="input"
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={onFocus}
        disabled={disabled}
        sx={{
          width: "100%",
          bgcolor: "rgba(10, 10, 10, 0.6)",
          border: "1px solid",
          borderColor: hasError ? "#ff1744" : "rgba(255, 255, 255, 0.1)",
          borderRadius: "12px",
          color: "#fff",
          fontSize: 15,
          fontFamily: "inherit",
          py: 1.8,
          px: 2,
          outline: "none",
          resize: "none",
          transition: "all 0.3s ease",
          boxSizing: "border-box",
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "text",
          "&::placeholder": {
            color: "rgba(255, 255, 255, 0.3)",
            fontSize: 13,
          },
          "&:hover:not(:focus)": {
            borderColor: hasError ? "#ff1744" : "rgba(255, 255, 255, 0.2)",
            bgcolor: "rgba(15, 15, 15, 0.8)",
          },
          "&:focus": {
            bgcolor: "rgba(0, 0, 0, 0.8)",
            borderColor: hasError ? "#ff1744" : "#00ffcc",
            boxShadow: hasError
              ? "0 0 0 4px rgba(255, 23, 68, 0.1)"
              : "0 0 0 4px rgba(0, 255, 204, 0.1)",
          },
        }}
      />
      {helperText && (
        <Typography
          sx={{ color: "#ff1744", fontSize: 13, mt: 1, fontWeight: 500 }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

export default function CustomPersianDatePicker({
  name,
  label,
  placeholder = "",
  disabled = false,
  isRequired = false,
  timestampType = "ms",
  formik,
}) {
  const value = getIn(formik?.values, name);
  const error = getIn(formik?.errors, name);
  const touched = getIn(formik?.touched, name);
  const showError = Boolean(touched && error);

  // Convert stored timestamp back to Date object for picker display
  const dateValue = value
    ? new Date(timestampType === "seconds" ? value * 1000 : value)
    : null;

  const handleChange = (date) => {
    if (!date) {
      formik?.setFieldValue(name, null);
      return;
    }
    const rawValue = date?.valueOf?.();
    const timestamp =
      timestampType === "seconds" && rawValue
        ? Math.floor(rawValue / 1000)
        : (rawValue ?? null);

    formik?.setFieldValue(name, timestamp);
  };

  const handleClose = () => {
    formik?.setFieldTouched(name, true);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {label && (
        <Typography
          sx={{
            color: showError ? "#ff1744" : "rgba(255,255,255,0.8)",
            fontSize: 14,
            fontWeight: 700,
            mb: 1.5,
            lineHeight: 1.6,
            transition: "color 0.3s ease",
          }}
        >
          {label}
          {isRequired && (
            <Typography component="span" sx={{ color: "#ff1744", mr: 0.5 }}>
              *
            </Typography>
          )}
        </Typography>
      )}

      <DatePicker
        value={dateValue}
        onChange={handleChange}
        onClose={handleClose}
        calendar={persian}
        locale={persian_fa}
        calendarPosition="bottom-right"
        format="YYYY/MM/DD"
        disabled={disabled}
        containerStyle={{ width: "100%" }}
        render={(inputValue, openCalendar, handleValueChange) => (
          <DatePickerInput
            value={inputValue}
            onFocus={openCalendar}
            onChange={handleValueChange}
            placeholder={placeholder}
            disabled={disabled}
            hasError={showError}
            helperText={showError ? error : ""}
          />
        )}
      />
    </Box>
  );
}
